/**
 * UserGuidesAdminActions — Admin-only Edit / Delete / Create controls
 * for both User Guides and Documentation Deliverables.
 *
 * Each component manages its own dialog state and posts to the new
 * admin_content endpoints. Use them inside UserGuides.js, gated on
 * the current user's role.
 */
import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Pencil, Trash2, Plus, Loader2, AlertTriangle } from 'lucide-react';
import { BACKEND_URL } from '../config';

const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const ROLE_OPTIONS = [
  { value: 'all', label: 'All Users' },
  { value: 'administrator', label: 'Administrator' },
  { value: 'project_manager', label: 'Project Manager' },
  { value: 'investor', label: 'Investor' },
  { value: 'social_actor', label: 'Social Actor' },
  { value: 'guest', label: 'Guest' },
];

// ── shared confirm dialog ─────────────────────────────────────────────
const ConfirmDelete = ({ open, onClose, onConfirm, title, busy }) => (
  <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-red-700">
          <AlertTriangle className="h-5 w-5" />
          Confirm deletion
        </DialogTitle>
        <DialogDescription>
          You are about to delete <strong>{title}</strong>. This cannot be undone for custom items;
          built-in items will be hidden but can be restored by editing them again.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={busy} data-testid="confirm-delete-cancel">
          Cancel
        </Button>
        <Button
          variant="destructive"
          onClick={onConfirm}
          disabled={busy}
          data-testid="confirm-delete-confirm"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
          Delete
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// ─────────────────────────────────────────────────────────────────────
//                       USER GUIDE — Edit / Create
// ─────────────────────────────────────────────────────────────────────
const GuideForm = ({ open, onClose, guide, onSaved }) => {
  // guide=null → create. guide=object → edit (full guide fetched separately).
  const isCreate = !guide;
  const [title, setTitle] = useState(guide?.title || '');
  const [description, setDescription] = useState(guide?.description || '');
  const [role, setRole] = useState(guide?.role || 'all');
  const [pages, setPages] = useState(guide?.pages || '');
  const [contentEn, setContentEn] = useState('');
  const [contentEl, setContentEl] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingContent, setLoadingContent] = useState(false);

  // Load the existing markdown content when editing.
  React.useEffect(() => {
    if (!open || isCreate || !guide?.id) return;
    setLoadingContent(true);
    const fetchLang = async (lang) => {
      try {
        const { data } = await axios.get(`${API}/user-guides/${guide.id}?lang=${lang}`, { headers: authHeaders() });
        return data?.content || '';
      } catch {
        return '';
      }
    };
    (async () => {
      const [en, el] = await Promise.all([fetchLang('en'), fetchLang('el')]);
      setContentEn(en);
      setContentEl(el);
      setLoadingContent(false);
    })();
    setTitle(guide.title || '');
    setDescription(guide.description || '');
    setRole(guide.role || 'all');
    setPages(guide.pages || '');
  }, [open, isCreate, guide]);

  const save = async () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (isCreate && !contentEn.trim()) {
      toast.error('English content is required to create a new guide');
      return;
    }
    setLoading(true);
    try {
      const body = {
        title: title.trim(),
        description: description.trim(),
        role,
        pages: pages.trim() || undefined,
        content_en: contentEn,
        content_el: contentEl,
      };
      if (isCreate) {
        await axios.post(`${API}/user-guides/custom`, body, { headers: authHeaders() });
        toast.success('New guide created');
      } else {
        await axios.patch(`${API}/user-guides/${guide.id}`, body, { headers: authHeaders() });
        toast.success('Guide updated');
      }
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save guide');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isCreate ? 'Create new user guide' : `Edit guide — ${guide?.title}`}</DialogTitle>
          <DialogDescription>
            {isCreate
              ? 'Add a brand-new user guide. Stored in the database and served alongside the built-in guides.'
              : 'Edit the title, description, role and markdown content. For built-in guides this saves an override; the original on-disk file is not modified.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
          <div className="md:col-span-2">
            <Label htmlFor="guide-title">Title</Label>
            <Input
              id="guide-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="guide-form-title"
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="guide-desc">Description</Label>
            <Textarea
              id="guide-desc"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="guide-form-description"
            />
          </div>
          <div>
            <Label>Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="guide-form-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="guide-pages">Pages (display label)</Label>
            <Input
              id="guide-pages"
              placeholder="e.g. 8+ pages"
              value={pages}
              onChange={(e) => setPages(e.target.value)}
              data-testid="guide-form-pages"
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="guide-en">Markdown content (English) {loadingContent && <span className="text-xs text-slate-500">(loading…)</span>}</Label>
            <Textarea
              id="guide-en"
              rows={14}
              className="font-mono text-xs"
              value={contentEn}
              onChange={(e) => setContentEn(e.target.value)}
              placeholder="# Title&#10;&#10;Markdown supported…"
              data-testid="guide-form-content-en"
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="guide-el">Markdown content (Greek) — optional</Label>
            <Textarea
              id="guide-el"
              rows={10}
              className="font-mono text-xs"
              value={contentEl}
              onChange={(e) => setContentEl(e.target.value)}
              placeholder="Leave empty to keep current Greek companion or generate later via AI translation."
              data-testid="guide-form-content-el"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading} data-testid="guide-form-cancel">Cancel</Button>
          <Button onClick={save} disabled={loading} data-testid="guide-form-save">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {isCreate ? 'Create guide' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AdminGuideButtons = ({ guide, onChanged }) => {
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const doDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/user-guides/${guide.id}`, { headers: authHeaders() });
      toast.success(guide.is_custom ? 'Guide deleted' : 'Guide hidden');
      onChanged && onChanged();
      setShowDelete(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-1 mt-2 pt-2 border-t border-dashed border-amber-200 bg-amber-50/40 -mx-2 px-2 py-1.5 rounded">
        <span className="text-xs uppercase tracking-wider text-amber-800 font-medium mr-auto">
          Admin
        </span>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs text-amber-800 hover:bg-amber-100"
          onClick={() => setShowEdit(true)}
          data-testid={`admin-edit-guide-${guide.id}`}
        >
          <Pencil className="h-3 w-3 mr-1" /> Edit
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs text-red-700 hover:bg-red-100"
          onClick={() => setShowDelete(true)}
          data-testid={`admin-delete-guide-${guide.id}`}
        >
          <Trash2 className="h-3 w-3 mr-1" /> Delete
        </Button>
      </div>

      {showEdit && (
        <GuideForm
          open={showEdit}
          onClose={() => setShowEdit(false)}
          guide={guide}
          onSaved={onChanged}
        />
      )}
      <ConfirmDelete
        open={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={doDelete}
        title={guide.title}
        busy={deleting}
      />
    </>
  );
};

export const NewGuideButton = ({ onChanged }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="outline"
        className="border-blue-300 text-blue-700 hover:bg-blue-50"
        onClick={() => setOpen(true)}
        data-testid="admin-new-guide-btn"
      >
        <Plus className="h-4 w-4 mr-2" /> New Guide
      </Button>
      {open && (
        <GuideForm
          open={open}
          onClose={() => setOpen(false)}
          guide={null}
          onSaved={onChanged}
        />
      )}
    </>
  );
};


// ─────────────────────────────────────────────────────────────────────
//                  DOCUMENTATION DELIVERABLE — Edit / Create
// ─────────────────────────────────────────────────────────────────────
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve((r.result || '').toString().split(',')[1] || '');
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const DeliverableForm = ({ open, onClose, deliverable, onSaved }) => {
  const isCreate = !deliverable;
  const [titleEn, setTitleEn] = useState(deliverable?.title_en || '');
  const [titleEl, setTitleEl] = useState(deliverable?.title_el || '');
  const [description, setDescription] = useState(deliverable?.description || '');
  const [version, setVersion] = useState(deliverable?.version || '1.0');
  const [lastUpdated, setLastUpdated] = useState(deliverable?.last_updated || '');
  const [fileEn, setFileEn] = useState(null);
  const [fileEl, setFileEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const enRef = useRef(null);
  const elRef = useRef(null);

  React.useEffect(() => {
    if (!open) return;
    setTitleEn(deliverable?.title_en || '');
    setTitleEl(deliverable?.title_el || '');
    setDescription(deliverable?.description || '');
    setVersion(deliverable?.version || '1.0');
    setLastUpdated(deliverable?.last_updated || '');
    setFileEn(null);
    setFileEl(null);
  }, [open, deliverable]);

  const save = async () => {
    if (!titleEn.trim() && !titleEl.trim()) {
      toast.error('At least one title (EN or EL) is required');
      return;
    }
    setLoading(true);
    try {
      const body = {
        title_en: titleEn.trim() || undefined,
        title_el: titleEl.trim() || undefined,
        description: description.trim() || undefined,
        version: version.trim() || undefined,
        last_updated: lastUpdated.trim() || undefined,
      };
      if (fileEn) {
        body.file_en_base64 = await fileToBase64(fileEn);
        body.file_en_filename = fileEn.name;
      }
      if (fileEl) {
        body.file_el_base64 = await fileToBase64(fileEl);
        body.file_el_filename = fileEl.name;
      }
      if (isCreate) {
        await axios.post(`${API}/documentation-deliverables`, body, { headers: authHeaders() });
        toast.success('New documentation deliverable created');
      } else {
        await axios.patch(`${API}/documentation-deliverables/${deliverable.id}`, body, { headers: authHeaders() });
        toast.success('Deliverable updated');
      }
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save deliverable');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isCreate ? 'New documentation deliverable' : `Edit — ${deliverable?.title_en || deliverable?.title_el}`}</DialogTitle>
          <DialogDescription>
            Documentation cards appear in the &quot;Documentation Centre&quot; section. Upload PDF or Word
            files; max 25 MB per file. Either language is optional but at least one title is required.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
          <div>
            <Label htmlFor="d-title-en">Title (English)</Label>
            <Input id="d-title-en" value={titleEn} onChange={(e) => setTitleEn(e.target.value)} data-testid="deliv-form-title-en" />
          </div>
          <div>
            <Label htmlFor="d-title-el">Title (Greek)</Label>
            <Input id="d-title-el" value={titleEl} onChange={(e) => setTitleEl(e.target.value)} data-testid="deliv-form-title-el" />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="d-desc">Description</Label>
            <Textarea id="d-desc" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="deliv-form-description" />
          </div>
          <div>
            <Label htmlFor="d-ver">Version</Label>
            <Input id="d-ver" value={version} onChange={(e) => setVersion(e.target.value)} data-testid="deliv-form-version" />
          </div>
          <div>
            <Label htmlFor="d-upd">Last updated (display)</Label>
            <Input id="d-upd" placeholder="e.g. Jun 2026" value={lastUpdated} onChange={(e) => setLastUpdated(e.target.value)} data-testid="deliv-form-last-updated" />
          </div>

          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t">
            <div>
              <Label className="block">English file (.pdf / .docx)</Label>
              <input
                ref={enRef}
                type="file"
                accept=".pdf,.docx,.doc"
                onChange={(e) => setFileEn(e.target.files?.[0] || null)}
                className="text-xs"
                data-testid="deliv-form-file-en"
              />
              {fileEn && <p className="text-xs text-emerald-700 mt-1">Selected: {fileEn.name}</p>}
              {!fileEn && deliverable?.file_en_url && (
                <p className="text-xs text-slate-500 mt-1">Current: {deliverable.file_en_filename || deliverable.file_en_url.split('/').pop()}</p>
              )}
            </div>
            <div>
              <Label className="block">Greek file (.pdf / .docx)</Label>
              <input
                ref={elRef}
                type="file"
                accept=".pdf,.docx,.doc"
                onChange={(e) => setFileEl(e.target.files?.[0] || null)}
                className="text-xs"
                data-testid="deliv-form-file-el"
              />
              {fileEl && <p className="text-xs text-emerald-700 mt-1">Selected: {fileEl.name}</p>}
              {!fileEl && deliverable?.file_el_url && (
                <p className="text-xs text-slate-500 mt-1">Current: {deliverable.file_el_filename || deliverable.file_el_url.split('/').pop()}</p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading} data-testid="deliv-form-cancel">Cancel</Button>
          <Button onClick={save} disabled={loading} data-testid="deliv-form-save">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {isCreate ? 'Create deliverable' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AdminDeliverableButtons = ({ deliverable, onChanged }) => {
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const doDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/documentation-deliverables/${deliverable.id}`, { headers: authHeaders() });
      toast.success('Deliverable deleted');
      onChanged && onChanged();
      setShowDelete(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-1 mt-2 pt-2 border-t border-dashed border-amber-200">
        <span className="text-xs uppercase tracking-wider text-amber-800 font-medium mr-auto">
          Admin
        </span>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs text-amber-800 hover:bg-amber-100"
          onClick={() => setShowEdit(true)}
          data-testid={`admin-edit-deliv-${deliverable.id}`}
        >
          <Pencil className="h-3 w-3 mr-1" /> Edit
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs text-red-700 hover:bg-red-100"
          onClick={() => setShowDelete(true)}
          data-testid={`admin-delete-deliv-${deliverable.id}`}
        >
          <Trash2 className="h-3 w-3 mr-1" /> Delete
        </Button>
      </div>

      {showEdit && (
        <DeliverableForm
          open={showEdit}
          onClose={() => setShowEdit(false)}
          deliverable={deliverable}
          onSaved={onChanged}
        />
      )}
      <ConfirmDelete
        open={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={doDelete}
        title={deliverable.title_en || deliverable.title_el}
        busy={deleting}
      />
    </>
  );
};

export const NewDeliverableButton = ({ onChanged }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="outline"
        className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
        onClick={() => setOpen(true)}
        data-testid="admin-new-deliverable-btn"
      >
        <Plus className="h-4 w-4 mr-2" /> New Documentation
      </Button>
      {open && (
        <DeliverableForm
          open={open}
          onClose={() => setOpen(false)}
          deliverable={null}
          onSaved={onChanged}
        />
      )}
    </>
  );
};
